import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  BarChart2,
  PlayCircle,
  UserPlus,
  Plus,
  RefreshCw,
  CheckCircle,
  Circle,
  ChevronDown,
  ChevronRight,
  Award,
  AlertTriangle,
  Trash2,
  BookMarked,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { apiClient } from "../services/api";

const TABS = [
  { id: "dashboard", icon: BarChart2 },
  { id: "catalogue", icon: BookOpen },
  { id: "my-courses", icon: PlayCircle },
  { id: "assign", icon: UserPlus },
  { id: "create", icon: Plus },
];

const CONTENT_TYPES = [
  { value: "text" },
  { value: "video" },
  { value: "pdf" },
  { value: "quiz" },
  { value: "link" },
];

const STATUS_VARIANTS = {
  assigned: "outline",
  in_progress: "default",
  completed: "secondary",
  failed: "destructive",
  expired: "destructive",
};

const statusBadge = (s, t) => {
  const variant = STATUS_VARIANTS[s] || "outline";
  return <Badge variant={variant}>{t(`lmsHub.status.${s}`, { defaultValue: s })}</Badge>;
};

const contentTypeLabel = (type, t) => t(`lmsHub.contentTypes.${type}`, { defaultValue: type });

const ProgressBar = ({ pct }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-green-500" : pct > 50 ? "bg-blue-500" : "bg-orange-400"}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
    <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
  </div>
);

// ---------------------------------------------------------------------------
// Tab: Dashboard
// ---------------------------------------------------------------------------
function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/lms/dashboard");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
      <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      {t("lmsHub.loading")}
    </div>
  );

  const kpis = [
    { label: t("lmsHub.dashboard.kpiPublishedCourses"), value: data.total_courses, icon: BookOpen },
    { label: t("lmsHub.dashboard.kpiInProgress"), value: data.in_progress, icon: PlayCircle },
    { label: t("lmsHub.dashboard.kpiCompletionRate"), value: `${data.completion_rate}%`, icon: CheckCircle },
    { label: t("lmsHub.dashboard.kpiAvgScore"), value: data.avg_score !== null ? `${data.avg_score}%` : "N/A", icon: Award },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2 mb-1">
              <k.icon className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </div>
            <p className="text-2xl font-bold">{k.value}</p>
          </div>
        ))}
      </div>

      {data.overdue > 0 && (
        <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 dark:bg-orange-950/20 dark:border-orange-800 rounded-lg text-sm text-orange-700 dark:text-orange-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t("lmsHub.dashboard.overdue", { count: data.overdue })}</span>
        </div>
      )}

      {data.top_courses?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
            {t("lmsHub.dashboard.topCourses")}
          </h3>
          <div className="space-y-2">
            {data.top_courses.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 border rounded-lg bg-card">
                <p className="text-sm font-medium">{c.title}</p>
                <span className="text-xs text-muted-foreground">{t("lmsHub.common.enrolledCount", { count: c.enrollments })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Catalogue
// ---------------------------------------------------------------------------
function CatalogueTab({ onAssign }) {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/lms/courses?published_only=false");
      setCourses(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = async (id, current) => {
    await apiClient.patch(`v1/lms/courses/${id}`, { is_published: !current });
    load();
  };

  const deleteCourse = async (id) => {
    if (!window.confirm(t("lmsHub.catalogue.confirmDelete"))) return;
    await apiClient.delete(`v1/lms/courses/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{t("lmsHub.catalogue.count", { count: total })}</span>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {courses.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">{t("lmsHub.catalogue.empty")}</p>
      ) : (
        <div className="space-y-2">
          {courses.map((c) => (
            <div key={c.id} className="border rounded-lg bg-card overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              >
                <div className="flex items-center gap-3">
                  {expanded === c.id ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{c.title}</p>
                      {c.is_mandatory && <Badge variant="destructive" className="text-xs">{t("lmsHub.catalogue.mandatory")}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {c.category || t("lmsHub.catalogue.uncategorized")} · {t("lmsHub.common.lessonCount", { count: c.lesson_count })}
                      {c.duration_minutes && ` · ${c.duration_minutes} min`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={c.is_published ? "default" : "outline"}>
                    {c.is_published ? t("lmsHub.catalogue.published") : t("lmsHub.catalogue.draft")}
                  </Badge>
                </div>
              </div>

              {expanded === c.id && (
                <div className="border-t px-4 pb-4 pt-3 bg-muted/10 space-y-3">
                  {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => togglePublish(c.id, c.is_published)}>
                      {c.is_published ? t("lmsHub.catalogue.unpublish") : t("lmsHub.catalogue.publish")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onAssign?.(c)}>
                      <UserPlus className="h-3.5 w-3.5 mr-1" /> {t("lmsHub.catalogue.assign")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteCourse(c.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Mes formations (employee self-view)
// ---------------------------------------------------------------------------
function MyCoursesTab() {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("v1/lms/assignments?limit=100");
      setAssignments(r.data.items || []);
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    if (detail[id]) return;
    try {
      const r = await apiClient.get(`v1/lms/assignments/${id}`);
      setDetail((prev) => ({ ...prev, [id]: r.data }));
    } catch {
      setDetail((prev) => ({ ...prev, [id]: { error: true } }));
    }
  };

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    await loadDetail(id);
  };

  const completeLesson = async (assignmentId, lessonId) => {
    await apiClient.post(`v1/lms/assignments/${assignmentId}/lessons/${lessonId}/complete`, {});
    const r = await apiClient.get(`v1/lms/assignments/${assignmentId}`);
    setDetail((prev) => ({ ...prev, [assignmentId]: r.data }));
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {assignments.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">{t("lmsHub.myCourses.empty")}</p>
      ) : (
        <div className="space-y-2">
          {assignments.map((a) => {
            const d = detail[a.id];
            const isOverdue = a.due_date && a.status !== "completed" && new Date(a.due_date) < new Date();
            return (
              <div key={a.id} className="border rounded-lg bg-card overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30"
                  onClick={() => toggle(a.id)}
                >
                  <div className="flex items-center gap-3">
                    {expanded === a.id ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <div>
                      <p className="font-medium text-sm">{a.course_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {a.employee_name}
                        {a.due_date && (
                          <span className={isOverdue ? " text-red-600 font-medium" : ""}>
                            {" · "}{t("lmsHub.myCourses.dueDate", { date: new Date(a.due_date).toLocaleDateString() })}
                          </span>
                        )}
                        {a.final_score !== null && a.final_score !== undefined && ` · ${t("lmsHub.myCourses.score", { score: a.final_score })}`}
                        {a.certificate_issued && ` · ${t("lmsHub.myCourses.certified")}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 hidden sm:block">
                      <ProgressBar pct={a.progress_pct} />
                    </div>
                    {statusBadge(a.status, t)}
                  </div>
                </div>

                {expanded === a.id && d && !d.error && d.lesson_progress !== undefined && (
                  <div className="border-t px-4 pb-4 pt-3 bg-muted/10">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("lmsHub.myCourses.lessons")}</p>
                    <div className="space-y-1">
                      {(d.lesson_progress || []).map((lp, idx) => {
                        const done = lp.is_completed;
                        return (
                          <div key={lp.lesson_id} className="flex items-center gap-3 py-1">
                            <button onClick={() => !done && completeLesson(a.id, lp.lesson_id)}>
                              {done ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                            <span className={`text-sm ${done ? "line-through text-muted-foreground" : ""}`}>
                              {t("lmsHub.myCourses.lessonNumber", { number: idx + 1 })}
                            </span>
                            {lp.quiz_score !== null && lp.quiz_score !== undefined && (
                              <span className="text-xs text-muted-foreground ml-auto">{t("lmsHub.myCourses.quizScore", { score: lp.quiz_score })}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Assigner
// ---------------------------------------------------------------------------
function AssignTab({ preselectedCourse }) {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    course_id: preselectedCourse?.id || "",
    employee_ids: [],
    due_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    apiClient.get("v1/lms/courses?published_only=false&limit=100").then((r) => setCourses(r.data.items || []));
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data || []));
  }, []);

  useEffect(() => {
    if (preselectedCourse) setForm((f) => ({ ...f, course_id: preselectedCourse.id }));
  }, [preselectedCourse]);

  const toggleEmployee = (id) => {
    setForm((f) => ({
      ...f,
      employee_ids: f.employee_ids.includes(id)
        ? f.employee_ids.filter((e) => e !== id)
        : [...f.employee_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.course_id || form.employee_ids.length === 0) return;
    setLoading(true);
    try {
      const r = await apiClient.post(`v1/lms/courses/${form.course_id}/assign`, {
        employee_ids: form.employee_ids,
        due_date: form.due_date || null,
      });
      setSuccess(t("lmsHub.assign.success", { count: r.data.assigned }));
      setForm((f) => ({ ...f, employee_ids: [], due_date: "" }));
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 max-w-lg">
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle className="h-4 w-4" /> {success}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">{t("lmsHub.assign.fieldCourse")}</label>
          <select
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.course_id}
            onChange={(e) => setForm({ ...form, course_id: Number(e.target.value) })}
          >
            <option value="">{t("lmsHub.assign.selectCourse")}</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">{t("lmsHub.assign.fieldDueDate")}</label>
          <input
            type="date"
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
        </div>
        <div>
          <label className="text-sm font-medium">{t("lmsHub.assign.fieldEmployees", { count: form.employee_ids.length })}</label>
          <div className="mt-1 border rounded-md max-h-48 overflow-y-auto divide-y">
            {employees.map((e) => (
              <label key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.employee_ids.includes(e.id)}
                  onChange={() => toggleEmployee(e.id)}
                />
                <span className="text-sm">{e.first_name} {e.last_name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{e.registration_number}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={submit} disabled={loading || !form.course_id || form.employee_ids.length === 0}>
        {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
        {t("lmsHub.assign.assignCta")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Create course
// ---------------------------------------------------------------------------
function CreateTab({ onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "", description: "", category: "", duration_minutes: "",
    is_mandatory: false,
  });
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const addLesson = () => {
    setLessons((prev) => [...prev, {
      title: "", content_type: "text", content_url: "", content_body: "", order_index: prev.length
    }]);
  };

  const updateLesson = (idx, field, val) => {
    setLessons((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  };

  const submit = async () => {
    if (!form.title.trim()) return;
    setLoading(true);
    try {
      await apiClient.post("v1/lms/courses", {
        ...form,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
        lessons: lessons.map((l, i) => ({ ...l, order_index: i, is_required: true })),
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCreated?.(); }, 1500);
      setForm({ title: "", description: "", category: "", duration_minutes: "", is_mandatory: false });
      setLessons([]);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle className="h-4 w-4" /> {t("lmsHub.create.success")}
        </div>
      )}

      <div className="space-y-3">
        <input
          className="w-full border rounded-md px-3 py-2 text-sm bg-background"
          placeholder={t("lmsHub.create.titlePlaceholder")}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <textarea
          className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
          rows={2}
          placeholder={t("lmsHub.create.descriptionPlaceholder")}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            className="border rounded-md px-3 py-2 text-sm bg-background"
            placeholder={t("lmsHub.create.categoryPlaceholder")}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <input
            type="number"
            className="border rounded-md px-3 py-2 text-sm bg-background"
            placeholder={t("lmsHub.create.durationPlaceholder")}
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_mandatory}
            onChange={(e) => setForm({ ...form, is_mandatory: e.target.checked })}
          />
          {t("lmsHub.create.mandatory")}
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{t("lmsHub.create.lessonsTitle", { count: lessons.length })}</h3>
          <Button variant="outline" size="sm" onClick={addLesson}>
            <Plus className="h-4 w-4 mr-1" /> {t("lmsHub.create.lesson")}
          </Button>
        </div>
        <div className="space-y-3">
          {lessons.map((l, idx) => (
            <div key={idx} className="p-3 border rounded-lg space-y-2">
              <div className="flex gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                  placeholder={t("lmsHub.create.lessonTitlePlaceholder")}
                  value={l.title}
                  onChange={(e) => updateLesson(idx, "title", e.target.value)}
                />
                <select
                  className="border rounded px-2 py-1 text-sm bg-background"
                  value={l.content_type}
                  onChange={(e) => updateLesson(idx, "content_type", e.target.value)}
                >
                  {CONTENT_TYPES.map((type) => <option key={type.value} value={type.value}>{contentTypeLabel(type.value, t)}</option>)}
                </select>
                <Button variant="ghost" size="sm" onClick={() => setLessons((prev) => prev.filter((_, i) => i !== idx))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {(l.content_type === "video" || l.content_type === "pdf" || l.content_type === "link") && (
                <input
                  className="w-full border rounded px-2 py-1 text-sm bg-background"
                  placeholder={t("lmsHub.create.urlPlaceholder")}
                  value={l.content_url}
                  onChange={(e) => updateLesson(idx, "content_url", e.target.value)}
                />
              )}
              {l.content_type === "text" && (
                <textarea
                  className="w-full border rounded px-2 py-1 text-sm bg-background resize-none"
                  rows={2}
                  placeholder={t("lmsHub.create.contentPlaceholder")}
                  value={l.content_body}
                  onChange={(e) => updateLesson(idx, "content_body", e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <Button onClick={submit} disabled={loading || !form.title.trim()}>
        {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <BookMarked className="h-4 w-4 mr-2" />}
        {t("lmsHub.create.createCta")}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Hub
// ---------------------------------------------------------------------------
export default function LMSHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const [refreshKey, setRefreshKey] = useState(0);
  const [assignCourse, setAssignCourse] = useState(null);

  const setTab = (id) => setSearchParams({ tab: id });

  const handleAssign = (course) => {
    setAssignCourse(course);
    setTab("assign");
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":  return <DashboardTab key={refreshKey} />;
      case "catalogue":  return <CatalogueTab onAssign={handleAssign} />;
      case "my-courses": return <MyCoursesTab key={refreshKey} />;
      case "assign":     return <AssignTab preselectedCourse={assignCourse} />;
      case "create":     return <CreateTab onCreated={() => { setRefreshKey((k) => k + 1); setTab("catalogue"); }} />;
      default:           return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          {t("lmsHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("lmsHub.subtitle")}
        </p>
      </div>

      <div className="border-b flex gap-1 overflow-x-auto">
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(`lmsHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
